import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Users, Copy, Star, Zap, Award, Crown, Shield, TrendingUp,
  UserPlus, CalendarDays, Gift, Trophy, Loader2, Share2, ChevronRight
} from "lucide-react";

const BADGE_ICONS = { community_builder: Users, squad_captain: Shield, community_captain: Crown };
const BADGE_COLORS = { community_builder: "bg-blue-100 text-blue-800", squad_captain: "bg-purple-100 text-purple-800", community_captain: "bg-amber-100 text-amber-800" };

export default function BuildTeam() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/build-team/my-team"),
      api.get("/build-team/rewards"),
      api.get("/build-team/leaderboard"),
    ]).then(([t, r, l]) => {
      setTeam(t.data);
      setRewards(r.data);
      setLeaderboard(l.data.leaderboard || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const copyLink = () => {
    if (team?.invite_link) {
      navigator.clipboard.writeText(team.invite_link);
      toast.success("Invite link copied! Share it with your friends.");
    }
  };

  const shareLink = () => {
    if (team?.invite_link && navigator.share) {
      navigator.share({ title: "Join my squad on SetPlay!", text: `${user?.name} invited you to play! Join SetPlay and book courts, join sessions, and build your sports community.`, url: team.invite_link });
    } else { copyLink(); }
  };

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="build-team-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0F3D2E] via-[#1a5c44] to-teal-900 p-6 sm:p-10 text-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-6 h-6 text-[#FBBF24]" />
            <span className="text-[#FBBF24] font-heading uppercase tracking-wider text-sm">Build Your Team</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl mb-3">Players Bring Players</h1>
          <p className="text-white/70 max-w-lg mb-6">Invite friends, build your squad, and earn rewards. Every player you bring earns you XP, booking credits, badges, and priority access.</p>

          {/* Invite Link */}
          <div className="flex gap-2 max-w-lg">
            <Input value={team?.invite_link || ""} readOnly className="bg-white/10 border-white/20 text-white placeholder:text-white/40 font-mono text-xs" data-testid="invite-link" />
            <Button onClick={copyLink} variant="outline" className="border-white/30 text-white hover:bg-white/10 shrink-0" data-testid="copy-invite">
              <Copy className="w-4 h-4 mr-1" />Copy
            </Button>
            <Button onClick={shareLink} className="bg-[#FBBF24] text-[#0F3D2E] hover:bg-[#F59E0B] shrink-0">
              <Share2 className="w-4 h-4 mr-1" />Share
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Card><CardContent className="p-4 text-center">
          <Users className="w-5 h-5 text-blue-500 mx-auto mb-1" />
          <p className="text-2xl font-heading font-bold">{team?.member_count || 0}</p>
          <p className="text-xs text-muted-foreground">Squad Members</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <Zap className="w-5 h-5 text-amber-500 mx-auto mb-1" />
          <p className="text-2xl font-heading font-bold">{team?.total_xp || 0}</p>
          <p className="text-xs text-muted-foreground">Total XP</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <Gift className="w-5 h-5 text-green-500 mx-auto mb-1" />
          <p className="text-2xl font-heading font-bold">₱{(team?.total_credits_earned || 0).toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">Credits Earned</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <Award className="w-5 h-5 text-purple-500 mx-auto mb-1" />
          <p className="text-2xl font-heading font-bold">{(team?.badges_earned || []).length}</p>
          <p className="text-xs text-muted-foreground">Badges</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <Star className="w-5 h-5 text-orange-500 mx-auto mb-1" />
          <p className="text-2xl font-heading font-bold">{team?.priority_tokens || 0}</p>
          <p className="text-xs text-muted-foreground">Priority Tokens</p>
        </CardContent></Card>
      </div>

      {/* How It Works */}
      <Card>
        <CardHeader><CardTitle className="font-heading text-base">How It Works</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            {[
              {step: "1", icon: Share2, title: "Share Your Link", desc: "Send your invite to friends"},
              {step: "2", icon: UserPlus, title: "They Register", desc: "Friend signs up via your link → +50 XP"},
              {step: "3", icon: CalendarDays, title: "They Book & Play", desc: "First booking → +100 XP +₱50 credits"},
              {step: "4", icon: Trophy, title: "Earn Rewards", desc: "Build your squad → badges + priority tokens"},
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-2 relative">
                  <s.icon className="w-6 h-6 text-primary" />
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FBBF24] text-[#0F3D2E] rounded-full text-[10px] font-bold flex items-center justify-center">{s.step}</span>
                </div>
                <p className="text-sm font-medium">{s.title}</p>
                <p className="text-xs text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Badges & Rewards */}
      <Card>
        <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><Award className="w-4 h-4 text-purple-500" />Badges & Milestones</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {Object.entries(team?.badges_config || {}).map(([key, badge]) => {
              const earned = (team?.badges_earned || []).includes(key);
              const Icon = BADGE_ICONS[key] || Award;
              return (
                <div key={key} className={`p-4 rounded-xl border-2 text-center ${earned ? "border-primary bg-primary/5" : "border-dashed border-slate-200 opacity-60"}`}>
                  <Icon className={`w-8 h-8 mx-auto mb-2 ${earned ? "text-primary" : "text-slate-300"}`} />
                  <p className="font-heading text-sm font-bold">{badge.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">{badge.desc}</p>
                  {earned && <Badge className="mt-2 bg-primary text-white">Earned!</Badge>}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* My Squad */}
        <Card>
          <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><Users className="w-4 h-4" />My Squad ({team?.member_count || 0})</CardTitle></CardHeader>
          <CardContent>
            {(team?.members || []).length === 0 ? (
              <div className="text-center py-6">
                <Users className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-30" />
                <p className="text-sm text-muted-foreground">No squad members yet. Share your invite link!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {team.members.map((m, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div>
                      <p className="text-sm font-medium">{m.name}</p>
                      <div className="flex gap-1 mt-0.5">{(m.sports || []).slice(0, 2).map(s => <Badge key={s} variant="outline" className="text-[8px] capitalize">{s}</Badge>)}</div>
                    </div>
                    <div className="text-right">
                      <Badge variant={m.status === "active" ? "default" : "secondary"} className="text-[9px]">{m.status}</Badge>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{m.total_bookings || 0} bookings</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Leaderboard */}
        <Card>
          <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" />Team Builder Leaderboard</CardTitle></CardHeader>
          <CardContent>
            {leaderboard.length === 0 ? (
              <p className="text-center text-muted-foreground py-6 text-sm">Be the first on the leaderboard!</p>
            ) : (
              <div className="space-y-2">
                {leaderboard.slice(0, 10).map((t, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold w-5 ${i < 3 ? "text-amber-500" : "text-muted-foreground"}`}>#{i + 1}</span>
                      <span className="text-sm">{t.captain_name}</span>
                      {t.badges?.map(b => <Badge key={b} className={`${BADGE_COLORS[b] || "bg-muted"} text-[8px]`}>{b.replace("_", " ")}</Badge>)}
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold">{t.total_xp} XP</p>
                      <p className="text-[10px] text-muted-foreground">{t.active_members}/{t.member_count} active</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Reward History */}
      {(rewards?.rewards || []).length > 0 && (
        <Card>
          <CardHeader><CardTitle className="font-heading text-base">Recent Rewards</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {rewards.rewards.slice(0, 8).map(r => (
                <div key={r.reward_id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                  <div>
                    <p className="text-sm">{r.description}</p>
                    <p className="text-[10px] text-muted-foreground">{r.detail} · {new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.xp > 0 && <Badge variant="outline" className="text-[9px]">+{r.xp} XP</Badge>}
                    {r.credits > 0 && <Badge className="bg-green-100 text-green-800 text-[9px]">+₱{r.credits}</Badge>}
                    {r.badge && <Badge className="bg-purple-100 text-purple-800 text-[9px]">{r.badge.replace("_", " ")}</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
